import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { TrackError } from '@fantaz/core/utils';
import { environment } from '@fantaz/shared/app-config';
import { USStates, USStatesAndCities } from '@fantaz/shared/data-access/api';
import { Address } from '@fantaz/shared/data-access/models';
import { zipCodeValidator } from '@fantaz/shared/utils/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, Observable, of, tap } from 'rxjs';
import {
  GoogleScriptInjectService,
  Maps,
} from './variants/google/google-script-inject.service';

export declare const google: any;

interface AddressForm {
  addressLine1: FormControl<string | null | undefined>;
  secondary: FormControl<string | null | undefined>;
  city: FormControl<string | null | undefined>;
  state: FormControl<string | null | undefined>;
  zipCode: FormControl<string | null | undefined>;
  entries: FormControl<number | null | undefined>;
  country: FormControl<string | null | undefined>;
}

type UndefinedBoolean = boolean | null | undefined;

@UntilDestroy()
@Component({
  selector: 'fz-field-address',
  templateUrl: './address-field-input.component.html',
  styleUrls: ['./address-field-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormFieldAddressComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FormFieldAddressComponent),
      multi: true,
    },
  ],
})
export class FormFieldAddressComponent
  implements ControlValueAccessor, Validator, OnInit, AfterViewInit
{
  scriptService = inject(GoogleScriptInjectService);
  @Input()
  readonly: boolean;

  @Input()
  id: string;

  /**
   * Form field touched property
   *
   * @default undefined
   * @type string
   */
  @Input()
  set touched(touched: UndefinedBoolean) {
    this._touched.set(touched);

    if (touched) {
      this.addressForm.markAllAsTouched();
    }
  }

  /**
   * Form field disabled property
   */
  _disabled: UndefinedBoolean;

  @Input()
  set disabled(disabled: UndefinedBoolean) {
    this._disabled = disabled;

    if (disabled) {
      this.addressForm.disable();
    }
  }

  get disabled(): boolean {
    return !!this._disabled;
  }

  /**
   * Form field required property
   *
   * @default false
   * @type boolean
   */
  @Input() required = false;

  showFullForm = true;
  @ViewChild('addressLine1') addressText: ElementRef;

  private readonly builder = inject(FormBuilder);

  addressForm = this.builder.group<AddressForm>({
    addressLine1: new FormControl<string>('', Validators.required),
    secondary: new FormControl<string>(''),
    city: new FormControl<string>('', Validators.required),
    state: new FormControl<string>('', Validators.required),
    zipCode: new FormControl<string | null>(null, [
      Validators.required,
      zipCodeValidator,
    ]),
    entries: new FormControl<number | null>(null),
    country: new FormControl<string>('US'),
  });

  /**
   * Prepare autocomplete data
   */
  states$: Observable<{ label: string; value: string }[]> = of(
    this._filterState(''),
  );

  cities = Object.keys(USStatesAndCities).map((el) => ({
    label: el,
    options: USStatesAndCities[el],
  }));

  cities$: Observable<{ label: string; options: string[] }[]> = of(
    this._filterCities(''),
  );

  config = {
    ...environment.autocomplete,
    enabled: false,
  };

  /**
   * Address signal
   */
  _touched = signal<UndefinedBoolean>(undefined);
  address = signal<Address>(Address.empty());
  fullAddress = computed<string>(() => {
    return this.address().fullAddress;
  });

  /**
   * Errors list for address form
   */
  errors = computed<ValidationErrors | null>(() => {
    const address = this.address();
    const touched = this._touched();

    // If field is not touched, do not show errors list for it yet (wait for
    // user to touch it) and return null instead of errors
    if (!touched) {
      return null;
    }

    // If field is required and address line is empty, return required error
    // and do not check other errors
    if (this.required && !this.addressForm.value.addressLine1) {
      return { requiredAddress: true };
    }

    // If address is not valid and field is required, return invalid error
    // and do not check other errors
    if (!address.isValid || !this.addressForm.valid) {
      return { invalidAddress: true };
    }

    return null;
  });

  isExpanded = signal<boolean>(false);
  wasInside = signal<boolean>(false);
  showHint = signal<boolean>(false);

  /**
   * Is valid signal.
   */
  isValid = computed(() => {
    const address = this.address();

    return address.isValid && this.addressForm.valid;
  });

  ngOnInit(): void {
    if (!environment.autocomplete.enabled) {
      this.showFullForm = true;
    }
  }

  ngAfterViewInit() {
    this.injectGoogleScript();
  }

  @HostListener('click')
  clickInside() {
    this.isExpanded.set(true);
  }

  @HostListener('document:click', ['$event'])
  clickout(event: MouseEvent) {
    const allowedCls = ['address-form-field', 'address-field'];
    const target = event.target as HTMLElement;
    const hasClass = allowedCls.some((cls) => target.className.includes(cls));

    if (!this.wasInside() && !hasClass) {
      this.isExpanded.set(false);
    }

    this.wasInside.set(false);
  }

  private injectGoogleScript() {
    if (!this.config.config.google?.apiKey) {
      throw new Error(
        'GoogleAutocompleteComponent: You must provide an API key',
      );
    }
    // inject the script
    const ref = this.scriptService.injectScript({
      apiKey: this.config.config.google?.apiKey,
    });

    // Subscribe to the script injection when map is loaded, and update the
    // widget state. If the script injection fails, update the widget state
    // accordingly and track the error in dynatrace.
    ref
      .pipe(
        catchError(() => {
          TrackError.trackCustomError(
            'autocomplete-address',
            'Google Maps API failed to load script',
            undefined,
            false,
          );
          return [];
        }),
        untilDestroyed(this),
      )
      .subscribe((maps: Maps) => {
        if (maps) {
          this.initializeGoogleAutocomplete(maps);
        }
      });
  }

  private initializeGoogleAutocomplete(maps: Maps) {
    const autocomplete = new maps.places.Autocomplete(
      this.addressText.nativeElement,
      {
        componentRestrictions: {
          country: ['us', 'pr'],
        },
        types: ['address'],
      },
    );

    google.maps.event.addListener(autocomplete, 'place_changed', () => {
      const place = autocomplete.getPlace();
      const address = this.scriptService.transformPlaceToAddress(place);
      this.writeValue(address);
      this.showHint.set(false);
    });

    setTimeout(() => {
      // fix google container position when page have a modal opened, material
      // dialog add a class to html and add top negative to handle overflow and
      // this cause that the google container have a bad position when calculate
      // the position for container when the modal is opened
      const html = document.querySelector('html');
      if (html?.classList.contains('cdk-global-scrollblock')) {
        const items = document.querySelectorAll('.pac-container');
        items.forEach((item) => {
          item.classList.add('position-fixed');
        });
      }
    }, 1500);
  }

  private _filterState(value: string): { label: string; value: string }[] {
    const filterValue = value.toLowerCase();
    return USStates.filter((option) =>
      option.name.toLowerCase().includes(filterValue),
    ).map((el) => ({
      label: `${el.name} (${el.abbreviation})`,
      value: el.abbreviation,
    }));
  }

  private _filterCities(value: string): { label: string; options: string[] }[] {
    const filterValue = value.toLowerCase();
    return this.cities.filter((group) => {
      return group.options.some((city) =>
        city.toLowerCase().includes(filterValue),
      );
    });
  }

  handleFocus() {
    this.isExpanded.set(true);
  }

  valueChangedFor(field: string, event: any) {
    const value = event.target.value;

    if (field === 'city') {
      this.cities$ = of(this._filterCities(value));
    }

    if (field === 'state') {
      this.states$ = of(this._filterState(value));
    }

    this.showHint.set(true);
  }

  onAddressSelected(address: Address) {
    this.writeValue(address);

    if (!address.isValid) {
      TrackError.trackError(new Error('User selected invalid address'));
    }
  }

  onAddressLineChanged(addressLine: string) {
    const address = this.address();
    if (address.fullAddress !== addressLine) {
      // reset address when address line change
      this.writeValue(Address.empty().copyWith({ addressLine1: addressLine }));
    }
  }

  onAutoCompleteError() {}

  registerOnChange(fn: any): void {
    this.addressForm.valueChanges
      .pipe(
        tap((value) => {
          this.address.update((v) =>
            v.copyWith({
              ...value,
            } as Address),
          );
        }),
        untilDestroyed(this),
      )
      .subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.addressForm.valueChanges.pipe(untilDestroyed(this)).subscribe(fn);
  }

  registerOnValidatorChange?(fn: () => void): void {}

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.addressForm.disable() : this.addressForm.enable();
  }

  writeValue(address?: Address): void {
    if (address) {
      if (!(address instanceof Address)) {
        address = new Address(address);
      }
      this.addressForm.patchValue(address);
      this.address.set(address);
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const address = this.address();

    if (!this._touched) {
      return null;
    }
    if (this.required && !this.addressForm.value.addressLine1) {
      return { requiredAddress: true };
    }

    if (this.addressForm.value.addressLine1 || this.required) {
      return this.addressForm.valid ? null : { invalidAddress: true };
    }

    if (!address.isValid && this.required) {
      return { invalidAddress: true };
    }

    return null;
  }

  onShowFullForm() {
    this.showFullForm = !this.showFullForm;
  }
}
